import "./ticket.scss";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import GreenRedOutlinedButtons from "../../buttons/greenRedOutlinedButtons/GreenRedOutlinedButtons";
import { useState, useId } from "react";
import dayjs from "dayjs";
import { getTicketRevenue } from "../../../utility/ticketRevenue";

function Ticket({
  ticket,
  editable,
  handleEdit,
  handleDelete,
  eventLink,
  onCustomizeTicket,
  paidCheckoutAmountSold,
  paidCheckoutOptionsSold,
  collapsible = false,
}) {
  const { t } = useTranslation("ticket");
  const [showDetails, setShowDetails] = useState(false);
  const detailsId = useId();
  const [showDescription, setShowDescription] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const hasOptions = Array.isArray(ticket.options) && ticket.options.length > 0;
  const isTicketTemplateCustomized = !!ticket.customTicketConfiguration;
  const hasTicketImage = !!ticket.image;
  const ticketRevenue = getTicketRevenue(ticket);
  const paidCheckoutTicketCount = Number.isFinite(Number(paidCheckoutAmountSold))
    ? Number(paidCheckoutAmountSold)
    : Number(ticket.amountSold || 0);
  const getPaidCheckoutOptionCount = (groupId, valueId, fallback) => {
    if (!paidCheckoutOptionsSold) return Number(fallback || 0);
    const value = paidCheckoutOptionsSold[`${groupId}:${valueId}`];
    return Number.isFinite(Number(value)) ? Number(value) : 0;
  };

  return (
    <div className="ticketContainer">
      <div className={`ticketHeader ${hasTicketImage ? "hasTicketImage" : ""}`}>
        <div className="ticketHeaderLeft">
          <div className="ticketThumbContainer">
            {hasTicketImage && (
              <div
                className="ticketThumbGlow"
                style={{ backgroundImage: `url(${ticket.image})` }}
                aria-hidden="true"
              />
            )}
            {hasTicketImage ? (
              <img src={ticket.image} alt={ticket.name} className="ticketThumb" />
            ) : (
              <svg
                viewBox="0 0 40 41"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="ticketThumbSvg"
                aria-hidden="true"
              >
                <g clipPath="url(#clip0_ticket_default)">
                  <path
                    d="M35.0002 5.875C35.4422 5.875 35.8661 6.05059 36.1787 6.36316C36.4912 6.67572 36.6668 7.09964 36.6668 7.54167V16.7083C35.5618 16.7083 34.502 17.1473 33.7206 17.9287C32.9392 18.7101 32.5002 19.7699 32.5002 20.875C32.5002 21.9801 32.9392 23.0399 33.7206 23.8213C34.502 24.6027 35.5618 25.0417 36.6668 25.0417V34.2083C36.6668 34.6504 36.4912 35.0743 36.1787 35.3868C35.8661 35.6994 35.4422 35.875 35.0002 35.875H5.00016C4.55814 35.875 4.13421 35.6994 3.82165 35.3868C3.50909 35.0743 3.3335 34.6504 3.3335 34.2083V25.0417C4.43856 25.0417 5.49837 24.6027 6.27977 23.8213C7.06118 23.0399 7.50016 21.9801 7.50016 20.875C7.50016 19.7699 7.06118 18.7101 6.27977 17.9287C5.49837 17.1473 4.43856 16.7083 3.3335 16.7083V7.54167C3.3335 7.09964 3.50909 6.67572 3.82165 6.36316C4.13421 6.05059 4.55814 5.875 5.00016 5.875H35.0002ZM33.3335 9.20833H6.66683V14.155L6.92683 14.29C8.05736 14.9067 9.01004 15.8042 9.69311 16.8959C10.3762 17.9877 10.7666 19.2369 10.8268 20.5233L10.8335 20.875C10.8337 22.2221 10.471 23.5443 9.78358 24.7028C9.09614 25.8612 8.10931 26.8131 6.92683 27.4583L6.66683 27.595V32.5417H33.3335V27.5933L33.0735 27.46C31.943 26.8433 30.9903 25.9458 30.3072 24.8541C29.6242 23.7623 29.2337 22.5131 29.1735 21.2267L29.1668 20.875C29.1668 18.035 30.7452 15.5633 33.0735 14.2917L33.3335 14.1533V9.20833ZM26.6668 15.875V25.875H13.3335V15.875H26.6668Z"
                    fill="#8BA4BF"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_ticket_default">
                    <rect width="40" height="40" fill="white" transform="translate(0 0.875)" />
                  </clipPath>
                </defs>
              </svg>
            )}
          </div>
          <p className="ticketName">{ticket.name}</p>
        </div>
        <p className="ticketRevenue">{ticketRevenue.toFixed(2)} €</p>
      </div>
      {collapsible && (
        <button
          type="button"
          className="ticketDetailsToggle"
          aria-expanded={showDetails}
          aria-controls={detailsId}
          onClick={() => setShowDetails((visible) => !visible)}
        >
          <span>{showDetails ? t("hideDetails") : t("details")}</span>
          <span aria-hidden="true">{showDetails ? "−" : "+"}</span>
        </button>
      )}
      <div className="ticketContent" id={detailsId} hidden={collapsible && !showDetails}>
        <div className="ticketMetrics">
          <div className="informationLine">
            <p className="informationName">{t("sold")}</p>
            <p className="informationValue">{paidCheckoutTicketCount}</p>
          </div>
          <div className="informationLine">
            <p className="informationName">{t("totalDistributed")}</p>
            <p className="informationValue">{ticket.amountSold}</p>
          </div>
          <div className="informationLine">
            <p className="informationName">{t("available")}</p>
            <p className="informationValue">
              {Math.max(
                0,
                Number(ticket.numAvailable || 0) -
                  Number(ticket.amountSold || 0) -
                  Number(ticket.reservedPromoQuantity || 0),
              )}
            </p>
          </div>
          <div className="informationLine">
            <p className="informationName">{t("price")}</p>
            <p className="informationValue">{ticket.price} €</p>
          </div>
          <div className="informationLine revenueMetric">
            <p className="informationName">{t("revenue")}</p>
            <p className="informationValue">{ticketRevenue.toFixed(2)} €</p>
          </div>
          <div className="informationLine">
            <p className="informationName">{t("secretTicket")}</p>
            <p className="informationValue">{ticket.secret ? t("yes") : t("no")}</p>
          </div>
          {ticket.availableFrom && ticket.availableTo && (
            <div className="informationLine availabilityMetric">
              <p className="informationName">{t("availability")}</p>
              <p className="informationValue">
                {dayjs(ticket.availableFrom).format("DD/MM/YYYY")} -{" "}
                {dayjs(ticket.availableTo).format("DD/MM/YYYY")}
              </p>
            </div>
          )}
        </div>
        {ticket.description && showDescription && (
          <div className="informationLine description">
            <p className="informationName">{t("desc")}: </p>
            <p className="informationValue">{ticket.description}</p>
          </div>
        )}
        {hasOptions && showOptions && (
          <div className="optionsList">
            {ticket.options.map((group) => (
              <div className="optionGroup" key={group.id || group.name}>
                <p className="optionGroupName">
                  {group.name}{" "}
                  {group.required ? `(${t("required")})` : `(${t("optional")})`}
                </p>
                <ul className="optionValues">
                  {(group.values || []).map((value) => (
                    <li key={value.id || value.label}>
                      <span>{value.label}</span>
                      <span className="optionSalesCounts">
                        <span className="optionMetric">
                          <small>{t("sold")}</small>
                          <strong>
                            {getPaidCheckoutOptionCount(group.id, value.id, value.amountSold)}
                          </strong>
                        </span>
                        <span className="optionMetric">
                          <small>{t("distributedShort")}</small>
                          <strong>{Number(value.amountSold || 0)}</strong>
                        </span>
                        {value.stock === null || value.stock === undefined
                          ? ""
                          : (
                            <span className="optionStock">
                              {t("stock")} {Number(value.stock) - Number(value.amountSold || 0)}
                            </span>
                          )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        {onCustomizeTicket && (
          <div className="informationLine">
            <p className="informationName">{t("ticketTemplate")}: </p>
            <p className="informationValue">
              {isTicketTemplateCustomized ? t("customizedTemplate") : t("usesGlobalTemplate")}
            </p>
          </div>
        )}

        <div className="buttonsContainer">
          {ticket.description && (
            <button
              className="showDescriptionBtn"
              onClick={() => setShowDescription(!showDescription)}
            >
              {showDescription ? t("hideDesc") : t("showDesc")}
            </button>
          )}
          {hasOptions && (
            <button className="showDescriptionBtn" onClick={() => setShowOptions(!showOptions)}>
              {showOptions ? t("hideOptions") : t("showOptions")}
            </button>
          )}
          {eventLink && (
            <a className="showSecretSaleBtn" href={eventLink} target="_blank" rel="noreferrer">
              {t("vipSale")}
            </a>
          )}
          {onCustomizeTicket && (
            <button
              className={`showDescriptionBtn customizePdfBtn ${
                isTicketTemplateCustomized ? "isCustomized" : ""
              }`}
              onClick={onCustomizeTicket}
            >
              <span>{t("customizePdf")}</span>
              {isTicketTemplateCustomized && (
                <span className="customizePdfStatus">
                  <span aria-hidden="true">✓</span>
                  {t("customizedTemplate")}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
      {editable && (
        <GreenRedOutlinedButtons
          greenName={t("edit")}
          redName={t("remove")}
          greenOnPress={handleEdit}
          redOnPress={handleDelete}
        />
      )}
    </div>
  );
}

Ticket.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    numAvailable: PropTypes.number.isRequired,
    amountSold: PropTypes.number.isRequired,
    reservedPromoQuantity: PropTypes.number,
    availableFrom: PropTypes.number,
    availableTo: PropTypes.number,
    description: PropTypes.string,
    image: PropTypes.string,
    customTicketConfiguration: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
      size: PropTypes.number,
      templatePath: PropTypes.string,
    }),
  }).isRequired,
  collapsible: PropTypes.bool,
  onCustomizeTicket: PropTypes.func,
  paidCheckoutAmountSold: PropTypes.number,
  paidCheckoutOptionsSold: PropTypes.objectOf(PropTypes.number),
};

export default Ticket;
